/**
 * Utility functions for handling query string parameters
 */

/**
 * Interface for parsed query parameters
 */
export interface ParsedQueryParams {
  [key: string]: string | string[];
  cp: string[]; // Array of cp parameters
}

/**
 * Parse the current URL query string into an object
 * @returns ParsedQueryParams object containing all query parameters
 */
export const parseQueryParams = (): ParsedQueryParams => {
  const url = new URL(window.location.href);
  const searchParams = new URLSearchParams(url.search);
  const result: ParsedQueryParams = {
    cp: [], // Initialize cp array
  };

  // Iterate through all search parameters
  searchParams.forEach((value, key) => {
    // Handle cp parameters (cp[0], cp[1], etc.)
    if (key.startsWith("cp[") && key.endsWith("]")) {
      // Extract the numeric index between "cp[" and "]"
      const indexStr = key.substring(3, key.length - 1);
      const index = parseInt(indexStr, 10);
      // Set the value at the correct index
      result.cp[index] = value;
    } else {
      // Handle other parameters
      if (result[key]) {
        // If parameter already exists, convert to array
        if (Array.isArray(result[key])) {
          (result[key] as string[]).push(value);
        } else {
          result[key] = [result[key] as string, value];
        }
      } else {
        result[key] = value;
      }
    }
  });

  return result;
};

/**
 * Convert a ParsedQueryParams object back to a query string
 * @param params ParsedQueryParams object
 * @returns Query string with properly URL encoded values
 */
export const buildQueryString = (params: ParsedQueryParams): string => {
  const parts: string[] = [];

  // Add cp parameters
  if (params.cp && params.cp.length > 0) {
    params.cp.forEach((value, index) => {
      // URL encode the value to handle special characters like &, :, etc.
      const encodedValue = encodeURIComponent(value);
      parts.push(`cp[${index}]=${encodedValue}`);
    });
  }

  // Add other parameters
  Object.entries(params).forEach(([key, value]) => {
    if (key !== "cp") {
      // URL encode the key
      const encodedKey = encodeURIComponent(key);
      if (Array.isArray(value)) {
        value.forEach((v) => {
          // URL encode each array value
          const encodedValue = encodeURIComponent(v);
          parts.push(`${encodedKey}=${encodedValue}`);
        });
      } else {
        // URL encode the value
        const encodedValue = encodeURIComponent(value);
        parts.push(`${encodedKey}=${encodedValue}`);
      }
    }
  });

  return parts.join("&");
};

/**
 * Update the URL with new query parameters
 * @param params ParsedQueryParams object
 */
export const updateQueryParams = (params: ParsedQueryParams): void => {
  const url = new URL(window.location.href);
  const queryString = buildQueryString(params);
  const newUrl = `${url.pathname}${queryString ? `?${queryString}` : ""}`;

  // Use history.pushState to update URL without page reload
  window.history.pushState({}, "", newUrl);
};

/**
 * Get a specific query parameter value
 * @param key Parameter key
 * @returns Parameter value or null if not found
 */
export const getQueryParam = (key: string): string | null => {
  const params = parseQueryParams();
  return (params[key] as string) || null;
};

/**
 * Get all cp parameters
 * @returns Array of cp parameter values
 */
export const getCpParams = (): string[] => {
  const params = parseQueryParams();
  return params.cp;
};
